import { Mark } from "@mantine/core";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { logger } from "utils/logger";

/**
 * Highlights matching text within a string
 * @param {string} text - The text to search within
 * @param {string} keyword - The keyword(s) to highlight (can be space-separated)
 * @returns {JSX.Element} - Text with highlighted matches
 */
export const TextHighlight = React.memo(({ text, keyword }) => {
  const keywordHighlighting = useSelector(
    state =>{ 
      return state.settingsReducer?.keywordHighlighting ?? false}
  );
  
  const textStr = text ? String(text) : '';
  const trimmedKeyword = keyword?.trim() || '';
  
  // Memoize the regex pattern creation
  const { pattern, keywords } = useMemo(() => {
    if (!trimmedKeyword) {
      return { pattern: null, keywords: [] };
    }
    const keywords = trimmedKeyword.toLowerCase().split(/\s+/);
    const escapedKeywords = keywords.map(k => 
      k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const pattern = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');
    return { pattern, keywords };
  }, [trimmedKeyword]);
  
  // Memoize the parts split
  const parts = useMemo(() => {
    if (!textStr || !pattern) return [textStr];
    return textStr.split(pattern);
  }, [textStr, pattern]);
  
  // Early returns after all hooks
  if (!text) return null;
  if (!trimmedKeyword || !keywordHighlighting) {
    return <>{text}</>;
  }
  
  return (
    <>
      {parts.map((part, index) => {
        // Check if this part matches any keyword (case-insensitive)
        const isMatch = keywords.some(k => 
          part.toLowerCase() === k.toLowerCase()
        );
        
        return isMatch ? (
          <Mark key={index} color="yellow">
            {part}
          </Mark>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        );
      })}
    </>
  );
});

TextHighlight.displayName = 'TextHighlight';

/**
 * Utility function to wrap text with highlighting
 * Useful for when you need the highlighted text but not in a component
 */
export const highlightText = (text, keyword) => {
  return <TextHighlight text={text} keyword={keyword} />;
};

export default TextHighlight;
